import React from 'react';
import { Helmet } from 'react-helmet-async';

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbName?: string;
  breadcrumbParent?: { name: string; path: string };
}

const BASE_URL = 'https://www.askaspen.app';

const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  noindex = false,
  jsonLd,
  breadcrumbName,
  breadcrumbParent,
}) => {
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const ogImageUrl = `${BASE_URL}/og-image.png`;

  const breadcrumbSchema = breadcrumbName
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: BASE_URL,
          },
          ...(breadcrumbParent
            ? [
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: breadcrumbParent.name,
                  item: `${BASE_URL}${breadcrumbParent.path}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: breadcrumbName,
                },
              ]
            : [
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: breadcrumbName,
                },
              ]),
        ],
      }
    : null;

  const allJsonLd = [
    ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
  ];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Ask Aspen" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD */}
      {allJsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default PageSEO;
